/**
 * Reading the pen, the finger and the mouse (docs/03-ARCHITECTURE.md §7.3).
 *
 * Pointer Events only — Mouse Events carry no pressure, no tilt and no stylus flag.
 * `getCoalescedEvents()` hands back every sample the tablet took between two frames,
 * which is what keeps a fast line smooth instead of a chain of straight dashes.
 *
 * The element must also carry `touch-action: none`, or the browser swallows the
 * gesture before it ever reaches this code.
 */

import type { ViewportState } from '@opal/contracts';

import type { InkSample } from './ink-session.js';

export interface PointerInputHandlers {
  readonly onBegin: (sample: InkSample) => void;
  readonly onExtend: (samples: readonly InkSample[]) => void;
  readonly onFinish: () => void;
  /** Read fresh on every event, so drawing keeps up with zoom and pan. */
  readonly viewport: () => ViewportState;
  /** False while the hand is moving the paper instead of writing on it. */
  readonly canDraw?: () => boolean;
}

/** The part of a pointer event this file reads. */
export interface PointerLike {
  readonly clientX: number;
  readonly clientY: number;
  readonly pressure: number;
  readonly timeStamp: number;
}

/** A pointer event that may or may not be able to report its in-between samples. */
export interface CoalescingPointer extends PointerLike {
  getCoalescedEvents?: () => readonly PointerLike[];
}

function toSample(
  event: PointerLike,
  bounds: DOMRect,
  viewport: ViewportState,
): InkSample {
  const canvas = viewport.screenToCanvas({
    x: event.clientX - bounds.left,
    y: event.clientY - bounds.top,
  });
  return { x: canvas.x, y: canvas.y, pressure: event.pressure, at: event.timeStamp };
}

/**
 * Buttons and fields that sit over the canvas — the "Undo snap" pill, the text field
 * a sticky note is typed into. Marked, because a press on one of them is not a mark
 * on the board.
 *
 * They cannot defend themselves. React listens for events at the top of the page,
 * above this element, so a button's own `onPointerDown` runs AFTER this listener has
 * already seen the press — by which time the pointer has been captured by the canvas,
 * and a captured pointer sends its click to whatever captured it. The button would
 * never be pressed at all. Asking what was actually under the finger is what stops
 * that, and it has to be asked here.
 */
export const BOARD_CHROME_SELECTOR = '[data-board-ui]';

function isBoardChrome(target: EventTarget | null): boolean {
  if (typeof Element === 'undefined' || !(target instanceof Element)) return false;
  return target.closest(BOARD_CHROME_SELECTOR) !== null;
}

/** True for input that should draw. A finger draws; a right or middle click does not. */
function isDrawing(event: PointerEvent): boolean {
  return event.button === 0 || event.pointerType === 'pen' || event.pointerType === 'touch';
}

/**
 * Belt and braces for touchscreens.
 *
 * `touch-action: none` should be enough to stop the browser treating a drawing
 * gesture as a scroll, but several mobile browsers ignore it and take the touch away
 * mid-stroke — which arrives here as a pointercancel and leaves the writer with a
 * single dot. Cancelling the touch event's default as well is what actually holds on
 * every device. Input is still read from Pointer Events only; nothing is read here.
 */
function holdTouchGestures(element: HTMLElement): () => void {
  const hold = (event: TouchEvent): void => {
    // Cancelling a touch on a button cancels the tap that would have pressed it.
    if (isBoardChrome(event.target)) return;
    if (event.cancelable) event.preventDefault();
  };
  element.addEventListener('touchstart', hold, { passive: false });
  element.addEventListener('touchmove', hold, { passive: false });
  return () => {
    element.removeEventListener('touchstart', hold);
    element.removeEventListener('touchmove', hold);
  };
}

/**
 * Keeps the samples coming even when the hand leaves the canvas mid-stroke.
 *
 * Some mobile browsers refuse capture for a touch; drawing must carry on regardless,
 * so a refusal is never allowed to swallow the stroke.
 */
function capture(element: HTMLElement, pointerId: number): void {
  try {
    element.setPointerCapture(pointerId);
  } catch {
    // Without capture the stroke simply ends if the finger leaves the canvas.
  }
}

/**
 * Every sample the device took since the last frame, not just the latest one. This is
 * what gives a graphics tablet its full rate instead of one point per frame.
 *
 * `getCoalescedEvents` is declared by the browser type definitions as always present,
 * and on some mobile browsers it simply is not — calling it there throws and, before
 * this guard existed, took the rest of the stroke with it: a single dot and nothing
 * else. One point per move is the correct fallback on those devices.
 */
export function samplesOf(event: CoalescingPointer): readonly PointerLike[] {
  try {
    const batch = event.getCoalescedEvents?.() ?? [];
    return batch.length > 0 ? batch : [event];
  } catch {
    return [event];
  }
}

/** Subscribes one pointer handler and returns its unsubscribe. */
function listenOn(
  element: HTMLElement,
  name: 'pointerdown' | 'pointermove' | 'pointerup' | 'pointercancel',
  handler: (event: PointerEvent) => void,
): () => void {
  element.addEventListener(name, handler);
  return () => {
    element.removeEventListener(name, handler);
  };
}

/** Attaches the drawing pipeline to an element. Returns the detach function. */
export function attachPointerInput(
  element: HTMLElement,
  handlers: PointerInputHandlers,
): () => void {
  let active: number | null = null;

  const down = (event: PointerEvent): void => {
    if (active !== null || !isDrawing(event)) return;
    if (isBoardChrome(event.target)) return;
    if (handlers.canDraw && !handlers.canDraw()) return;
    active = event.pointerId;
    capture(element, event.pointerId);
    handlers.onBegin(toSample(event, element.getBoundingClientRect(), handlers.viewport()));
  };

  const move = (event: PointerEvent): void => {
    if (active !== event.pointerId) return;
    const bounds = element.getBoundingClientRect();
    const viewport = handlers.viewport();
    const events = samplesOf(event);
    handlers.onExtend(events.map((sample) => toSample(sample, bounds, viewport)));
  };

  const up = (event: PointerEvent): void => {
    if (active !== event.pointerId) return;
    active = null;
    handlers.onFinish();
  };

  const cancel = (event: PointerEvent): void => {
    if (active !== event.pointerId) return;
    active = null;
    // The browser took the gesture away mid-stroke — a phone deciding late that the
    // touch was a scroll, for instance. Keep the ink already drawn rather than making
    // the student's work vanish; a stroke thrown away deliberately goes through the
    // ink session's own cancel, which is what a second finger uses.
    handlers.onFinish();
  };

  const stops = [
    listenOn(element, 'pointerdown', down),
    listenOn(element, 'pointermove', move),
    listenOn(element, 'pointerup', up),
    listenOn(element, 'pointercancel', cancel),
    holdTouchGestures(element),
  ];

  return () => {
    for (const stop of stops) stop();
  };
}
